import { List } from "@wayflyer/flyui";
import type { IntlShape } from "react-intl";

import type { KYCAIRType } from "../../api-types/air_schemas";
import type { KYCAIRQuestionType } from "../../api-types/staff_app_schemas";

import { KYC_AIR_MESSAGES, KYC_AIR_SUMMARY_MESSAGES } from "./messages";

export const airTypeQuestionParameters: Record<KYCAIRType, Partial<Record<KYCAIRQuestionType, string[]>>> = {
  precanned_question: {
    connection_between_legal_entity_and_brand: ["legal_name", "website"],
    proof_of_business_address: ["address"],
    missing_from_handels_register: [],
    missing_from_handels_register_general: [],
    proof_of_person_address_does_not_meet_requirements: ["name_of_person"],
    legal_proceedings: [],
    registry_status: ["status"],
    trading_history: [],
    generic_question: ["question_text"],
    //for now, we don't support manually adding Unit banking AIRs
  },
};

export const formatAirQuestionVariantText = (
  intl: IntlShape,
  questionType: KYCAIRQuestionType,
  opts: Record<string, string>
) => {
  const messageOpts = {
    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
    br: () => (
      // this adds an empty line between sentences
      <>
        <br />
        <br />
      </>
    ),
    ul: (chunks: string) => <List type="bullet">{chunks}</List>,
    li: (chunks: string) => <List.Item>{chunks}</List.Item>,
    ...opts,
  };

  switch (questionType) {
    case "connection_between_legal_entity_and_brand":
      return intl.formatMessage(KYC_AIR_MESSAGES.connectionBetweenLegalEntityAndBrand, messageOpts);
    case "proof_of_business_address":
      return intl.formatMessage(KYC_AIR_MESSAGES.proofOfBusinessAddress, messageOpts);
    case "missing_from_handels_register":
      return intl.formatMessage(KYC_AIR_MESSAGES.missingFromHandelsRegister, messageOpts);
    case "missing_from_handels_register_general":
      return intl.formatMessage(KYC_AIR_MESSAGES.missingFromHandelsRegisterGeneral, messageOpts);
    case "proof_of_person_address_does_not_meet_requirements":
      return intl.formatMessage(KYC_AIR_MESSAGES.proofOfPersonAddressDoesNotMeetRequirements, messageOpts);
    case "legal_proceedings":
      return intl.formatMessage(KYC_AIR_MESSAGES.legalProceedings, messageOpts);
    case "registry_status":
      return intl.formatMessage(KYC_AIR_MESSAGES.registryStatus, messageOpts);
    case "trading_history":
      return intl.formatMessage(KYC_AIR_MESSAGES.tradingHistory, messageOpts);
    case "generic_question":
      // no formatting possible since this is a custom question from the staff user
      return messageOpts["question_text"];
    case "unit_id_document":
    case "unit_passport":
      return intl.formatMessage(KYC_AIR_MESSAGES.unitPassport, messageOpts);
    case "unit_address_verification":
      return intl.formatMessage(KYC_AIR_MESSAGES.unitAddressVerification, messageOpts);
    case "unit_social_security_card":
      return intl.formatMessage(KYC_AIR_MESSAGES.unitSocialSecurityCard, messageOpts);
    case "unit_certificate_of_incorporation":
      return intl.formatMessage(KYC_AIR_MESSAGES.unitCertificateOfIncorporation, messageOpts);
    case "unit_employer_identification_number_confirmation":
      return intl.formatMessage(KYC_AIR_MESSAGES.unitEmployerIdentificationNumberConfirmation, messageOpts);
    case "unit_client_requested":
    case "unit_selfie_verification":
      // not properly supported and don't expect to get here so use the question text
      return messageOpts["question_text"];
    default: {
      // ensure no new types are not accounted for
      const _exhaustiveCheck: never = questionType;
      return _exhaustiveCheck;
    }
  }
};

export const getAirQuestionTypeText = (
  intl: IntlShape,
  questionType: KYCAIRQuestionType,
  opts: Record<string, string>,
  isForCustomerApp: boolean
) => {
  switch (questionType) {
    case "connection_between_legal_entity_and_brand":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.connectionBetweenLegalEntityAndBrand);
    case "proof_of_business_address":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.proofOfBusinessAddress);
    case "missing_from_handels_register":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.missingFromHandelsRegister);
    case "missing_from_handels_register_general":
      if (isForCustomerApp) {
        return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.missingFromHandelsRegister);
      } else {
        return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.missingFromHandelsRegisterGeneral);
      }
    case "proof_of_person_address_does_not_meet_requirements":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.proofOfPersonAddressDoesNotMeetRequirements);
    case "legal_proceedings":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.legalProceedings);
    case "registry_status":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.registryStatus);
    case "trading_history":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.tradingHistory);
    case "generic_question":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.genericQuestion);
    case "unit_id_document":
    case "unit_passport":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.unitPassport, opts);
    case "unit_address_verification":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.unitAddressVerification, opts);
    case "unit_social_security_card":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.unitSocialSecurityCard, opts);
    case "unit_certificate_of_incorporation":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.unitCertificateOfIncorporation, opts);
    case "unit_employer_identification_number_confirmation":
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.unitEmployerIdentificationNumberConfirmation, opts);
    case "unit_client_requested":
    case "unit_selfie_verification":
      // not properly supported and don't expect to get here so use the question text
      return intl.formatMessage(KYC_AIR_SUMMARY_MESSAGES.genericQuestion, opts);
    default: {
      // ensure no new types are not accounted for
      const _exhaustiveCheck: never = questionType;
      return _exhaustiveCheck;
    }
  }
};

export const getAirItemLabel = (
  intl: IntlShape,
  airRefId: string,
  questionType: KYCAIRQuestionType,
  opts: Record<string, string>,
  isForCustomerApp: boolean
): string => {
  return `[Ref ${airRefId}]: ${getAirQuestionTypeText(intl, questionType, opts, isForCustomerApp)}`;
};
